import React, { useContext, useEffect } from 'react';
import { Route, Switch, Prompt } from 'react-router-dom';
import { ProgressBarWithSteps } from '../progress-bar-with-steps/progress-bar-with-steps';
import { NavigationBarFooter } from '../navigation-bar-footer/navigation-bar-footer';
import { WizardContext } from '../../context/wizard-context';
import { Application, ApplicationAction, ApplicationActionType } from '../../types';
import { FullPageLoader } from 'src/components';
import { NavigationBarFooter as DPONavigationBarFooter } from '@vfs-digital-channels/ns-react-components';
import { assignDevicesRoute, yourDetailsRoute } from 'src/features/order/helpers/routes';
import './wizard.scss';

export type Page = React.ReactFragment;

export type WizardPageStep = {
  page: Page;
  route: string;
  group: string;
};

export type WizardProps = {
  basePath: string;
  automationIdPrefix: string;
  pageNameMap: Map<string, React.ReactFragment>;
  application: Application;
  dispatch: React.Dispatch<ApplicationAction>;
  dpo?: boolean;
};

export function Wizard({ application, dispatch, ...props }: WizardProps) {
  const {
    setOnNextEventHandler,
    setOnBackEventHandler,
    handleNavigateNext,
    handleNavigateBack,
    setNextButtonLabel,
    backButtonEnabled,
    nextButtonEnabled,
    nextButtonLabel,
    backButtonLabel,
    nextButtonVisible,
    backButtonVisible,
    footerVisible,
    isLoadingOverlayVisible,
    navigateBackEnabled,
    hideProgressBar,
    softDisable,
  } = useContext(WizardContext);
  const steps: WizardPageStep[] = (application?.steps || []).map((step) => {
    const pageComponent = props.pageNameMap.get(step.pageName);

    if (pageComponent) {
      return {
        page: pageComponent,
        route: step.route,
        group: step.groupName || step.pageName,
      };
    } else {
      throw new Error(`Wizard Revamp: unknown page name defined application: ${step.pageName}`);
    }
  });
  const currentStep = steps[application?.currentStepIndex || 0];

  const handleOnNext = () => dispatch({ type: ApplicationActionType.NAVIGATE_TO_NEXT_STEP });
  const handleOnBack = () => dispatch({ type: ApplicationActionType.NAVIGATE_TO_PREVIOUS_STEP });

  function getRouteWithBasePath(route) {
    return `${props.basePath}/${route}`;
  }

  useEffect(() => {
    // don't set default handler for assign device screen
    currentStep?.route !== assignDevicesRoute && setOnNextEventHandler(handleOnNext);
    setOnBackEventHandler(handleOnBack);
  }, [application?.currentStepIndex]);

  useEffect(() => {
    if (application?.quickEdit?.steps && application?.quickEdit?.steps?.length > 0) {
      setNextButtonLabel('Continue');
    } else if (
      application?.quickEdit?.steps?.length === 0 &&
      application?.quickEdit.returnIndex !== undefined
    ) {
      setNextButtonLabel('Update');
    }
  }, [application?.currentStepIndex, application?.quickEdit?.steps, nextButtonLabel]);

  const onBackOrExit = () => {
    const yourDetailsStep = application.steps?.findIndex((step) => step.route === yourDetailsRoute);

    if (application.tapOnPhoneJourney && application.currentStepIndex === yourDetailsStep) {
      window.close();
      return;
    }

    handleNavigateBack();
  };

  return application?.currentStepIndex === undefined ? (
    <FullPageLoader showLoader={true} />
  ) : (
    <div className="WizardRevamp">
      <Prompt
        when={true}
        message={(newUrl) => {
          if (application?.currentStepIndex !== undefined && application?.steps !== undefined) {
            const newStepIndex = steps.findIndex(
              (wizardStep: WizardPageStep) =>
                getRouteWithBasePath(wizardStep.route) === newUrl.pathname,
            );

            if (!navigateBackEnabled || newStepIndex === -1) {
              return false;
            } else if (newStepIndex > application.currentStepIndex) {
              handleOnNext();
            } else if (newStepIndex < application.currentStepIndex) {
              handleOnBack();
            }
          }

          return true;
        }}
      />
      <ProgressBarWithSteps
        steps={steps}
        hideProgressBar={hideProgressBar}
        currentStep={currentStep}
        automationIdPrefix={props.automationIdPrefix}
        onBack={onBackOrExit}
        hideBack={!backButtonVisible}
      />

      <FullPageLoader showLoader={isLoadingOverlayVisible} />

      <section className="page-content">
        <Switch>
          <Route
            path={getRouteWithBasePath(currentStep.route)}
            key={currentStep.route}
            component={currentStep.page}
          />
        </Switch>
      </section>

      {footerVisible && (
        <section className="footer-content">
          {props.dpo ? (
            <DPONavigationBarFooter
              onBack={onBackOrExit}
              onNext={handleNavigateNext}
              nextEnabled={nextButtonEnabled}
              backEnabled={!backButtonEnabled}
              nextButtonLabel={nextButtonLabel}
              backButtonLabel={backButtonLabel}
              showBack={backButtonVisible}
              showNext={nextButtonVisible}
              softDisable={softDisable}
            />
          ) : (
            <NavigationBarFooter
              onBack={onBackOrExit}
              onNext={handleNavigateNext}
              nextEnabled={nextButtonEnabled}
              backEnabled={backButtonEnabled}
              nextButtonLabel={nextButtonLabel}
              backButtonLabel={backButtonLabel}
              showBack={backButtonVisible}
              showNext={nextButtonVisible}
              softDisable={softDisable}
            />
          )}
        </section>
      )}
    </div>
  );
}
